import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { UnreadProvider } from "@/contexts/UnreadContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ChunkErrorBoundary from "@/components/ChunkErrorBoundary";
import { lazy, Suspense } from "react";
import ScrollToTop from "@/components/ScrollToTop";
import RootRoute from "@/components/RootRoute";
import { useSystemTheme } from "@/hooks/useSystemTheme";
import { usePageViewTracker } from "@/hooks/usePageViewTracker";


const Toaster = lazy(async () => {
  const module = await import("@/components/ui/toaster");
  return { default: module.Toaster };
});

const Sonner = lazy(async () => {
  const module = await import("@/components/ui/sonner");
  return { default: module.Toaster };
});

// Lazy load ALL other pages so their icon/animation deps don't block first paint
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const PublicProfile = lazy(() => import("./pages/PublicProfile"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Lazy load authenticated / heavier routes for code splitting

const Account = lazy(() => import("./pages/Account"));
const InboxPage = lazy(() => import("./pages/Inbox"));
const Analytics = lazy(() => import("./pages/Analytics"));
const Admin = lazy(() => import("./pages/Admin"));
const SubmitSuccess = lazy(() => import("./pages/SubmitSuccess"));
const Refer = lazy(() => import("./pages/Refer"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const AuthConfirm = lazy(() => import("./pages/AuthConfirm"));
const MfaVerify = lazy(() => import("./pages/MfaVerify"));
const VerifyRequest = lazy(() => import("./pages/VerifyRequest"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const AgenciesLanding = lazy(() => import("./pages/AgenciesLanding"));
const CreatorOnboarding = lazy(() => import("./pages/CreatorOnboarding"));
const AgencySignup = lazy(() => import("./pages/AgencySignup"));
const AgencyOnboarding = lazy(() => import("./pages/AgencyOnboarding"));
const AgencyDashboard = lazy(() => import("./pages/AgencyDashboard"));
const AgencyRoster = lazy(() => import("./pages/AgencyRoster"));
const AgencySettings = lazy(() => import("./pages/AgencySettings"));
const AgencyClaimInvite = lazy(() => import("./pages/AgencyClaimInvite"));
const AgencyJoinTeam = lazy(() => import("./pages/AgencyJoinTeam"));
const Feedback = lazy(() => import("./pages/Feedback"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogArticle = lazy(() => import("./pages/BlogArticle"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const AgencyLockGate = lazy(() => import("@/components/agency/AgencyLockGate"));
const BrandSignup = lazy(() => import("./pages/BrandSignup"));
const ForBrandsLanding = lazy(() => import("./pages/ForBrandsLanding"));
const BrandPending = lazy(() => import("./pages/BrandPending"));
const BrandOnboarding = lazy(() => import("./pages/BrandOnboarding"));
const BrandRejected = lazy(() => import("./pages/BrandRejected"));
const BrandInbox = lazy(() => import("./pages/BrandInbox"));
const BrandSend = lazy(() => import("./pages/BrandSend"));
const BrandSendDetail = lazy(() => import("./pages/BrandSendDetail"));
const BrandProfile = lazy(() => import("./pages/BrandProfile"));
const BrandAccount = lazy(() => import("./pages/BrandAccount"));
const BrandProtectedRoute = lazy(() => import("@/components/brand/BrandProtectedRoute"));


// Lazy load the unified app footer
const AppFooter = lazy(() => import("@/components/AppFooter"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60,
      gcTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
    },
  },
});

function PageViewTracker() {
  usePageViewTracker();
  return null;
}

const App = () => {
  useSystemTheme();

  return (
    <ChunkErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <BrowserRouter>
          <AuthProvider>
            <UnreadProvider>
              <PageViewTracker />
              <ScrollToTop />
              <main className="flex min-h-screen flex-col [overflow-x:clip]">
              <Suspense fallback={null}>
                <Routes>
                  <Route path="/" element={<RootRoute />} />
                  <Route path="/index" element={<Navigate to="/" replace />} />
                  <Route path="/signup" element={<Signup />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/auth/confirm" element={<AuthConfirm />} />
                  <Route path="/mfa-verify" element={<MfaVerify />} />
                  <Route path="/onboarding" element={<ProtectedRoute><CreatorOnboarding /></ProtectedRoute>} />
                  <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
                  <Route path="/inbox" element={<ProtectedRoute><InboxPage /></ProtectedRoute>} />
                  <Route path="/dashboard" element={<Navigate to="/inbox" replace />} />
                  <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
                  <Route path="/refer" element={<Refer />} />
                  <Route path="/refer/:code" element={<Refer />} />
                  <Route path="/verify-email" element={<VerifyEmail />} />
                  <Route path="/verify/:token" element={<VerifyRequest />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/unsubscribe" element={<Unsubscribe />} />
                  <Route path="/feedback" element={<Feedback />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:slug" element={<BlogArticle />} />
                  <Route path="/agencies" element={<AgenciesLanding />} />
                  <Route path="/agency" element={<Navigate to="/agency/inbox" replace />} />
                  <Route path="/signup/agency" element={<AgencySignup />} />
                  <Route path="/agency/onboarding" element={<ProtectedRoute><AgencyOnboarding /></ProtectedRoute>} />
                  <Route path="/agency/inbox" element={<ProtectedRoute><AgencyLockGate><AgencyDashboard /></AgencyLockGate></ProtectedRoute>} />
                  <Route path="/agency/dashboard" element={<Navigate to="/agency/inbox" replace />} />
                  <Route path="/agency/roster" element={<ProtectedRoute><AgencyLockGate><AgencyRoster /></AgencyLockGate></ProtectedRoute>} />
                  <Route path="/agency/settings" element={<ProtectedRoute><AgencyLockGate><AgencySettings /></AgencyLockGate></ProtectedRoute>} />
                  <Route path="/agency/claim/:token" element={<AgencyClaimInvite />} />
                  <Route path="/agency/join" element={<AgencyJoinTeam />} />
                  <Route path="/agency/*" element={<ProtectedRoute><NotFound /></ProtectedRoute>} />
                  <Route path="/for-brands" element={<ForBrandsLanding />} />
                  <Route path="/brands" element={<Navigate to="/for-brands" replace />} />
                  <Route path="/signup/brand" element={<BrandSignup />} />
                  <Route path="/brand/signup" element={<Navigate to="/signup/brand" replace />} />
                  <Route path="/brand/login" element={<Navigate to="/login?type=brand" replace />} />
                  <Route path="/brand/pending" element={<BrandProtectedRoute allowAnyStatus><BrandPending /></BrandProtectedRoute>} />
                  <Route path="/brand/onboarding" element={<BrandProtectedRoute allowAnyStatus><BrandOnboarding /></BrandProtectedRoute>} />
                  <Route path="/brand/rejected" element={<BrandProtectedRoute allowAnyStatus><BrandRejected /></BrandProtectedRoute>} />
                  <Route path="/brand" element={<BrandProtectedRoute><BrandInbox /></BrandProtectedRoute>} />
                  <Route path="/brand/new" element={<BrandProtectedRoute><BrandSend /></BrandProtectedRoute>} />
                  <Route path="/brand/sends/:id" element={<BrandProtectedRoute><BrandSendDetail /></BrandProtectedRoute>} />
                  <Route path="/brand/profile" element={<BrandProtectedRoute><BrandProfile /></BrandProtectedRoute>} />
                  <Route path="/brand/account" element={<BrandProtectedRoute><BrandAccount /></BrandProtectedRoute>} />
                  {/* Legacy redirects */}
                  <Route path="/brand/send" element={<Navigate to="/brand/new" replace />} />
                  <Route path="/brand/sends" element={<Navigate to="/brand" replace />} />
                  <Route path="/brand/discover" element={<Navigate to="/brand/new" replace />} />
                  <Route path="/brand/*" element={<BrandProtectedRoute><NotFound /></BrandProtectedRoute>} />

                  <Route path="/:slug/success" element={<SubmitSuccess />} />
                  <Route path="/:slug" element={<PublicProfile />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
                <Suspense fallback={null}>
                  <AppFooter />
                </Suspense>
              </Suspense>
              </main>
            </UnreadProvider>
          </AuthProvider>
          <Suspense fallback={null}>
            <Toaster />
            <Sonner />
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
    </ChunkErrorBoundary>
  );
};

export default App;
